import { useState } from "react";
import { Box, Stack, Typography, Button, Input, Chip } from "@mui/joy";
import { APIPaymentAgreement } from "../api";

const fmt = (v) => Number(v || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Интерактивный контрол для ИСПОЛНИТЕЛЯ (обмерщик/чертёжник).
 * props: eventId, role ('surveyor'|'draftsman'), agreement, expectedAmount, onChanged, notify
 */
export function AmountAgreementControl({ eventId, role, agreement, expectedAmount, onChanged, notify }) {
  const [state, setState] = useState(agreement || "");
  const [expected, setExpected] = useState(expectedAmount != null ? String(expectedAmount) : "");
  const [showInput, setShowInput] = useState(agreement === "disagree");
  const [busy, setBusy] = useState(false);

  const submit = async (choice, amount = null) => {
    setBusy(true);
    const r = await APIPaymentAgreement(eventId, role, choice, amount);
    setBusy(false);
    if (r?.error) { notify?.(r.error, "danger"); return; }
    setState(choice);
    if (choice === "disagree") setExpected(String(amount));
    onChanged?.();
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>Соответствует ли сумма?</Typography>

      {state === "" ? (
        <Stack direction="row" spacing={1}>
          <Button size="sm" variant="outlined" color="success" loading={busy}
                  onClick={() => { setShowInput(false); submit("agree"); }}>Да</Button>
          <Button size="sm" variant="outlined" color="danger"
                  onClick={() => setShowInput(true)}>Нет</Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Chip color={state === "agree" ? "success" : "danger"} variant="soft">
            {state === "agree" ? "Согласен с суммой" : `Не согласен · ожидал ${fmt(expected)} ₽`}
          </Chip>
          <Button size="sm" variant="plain" color="neutral"
                  onClick={() => { setState(""); setShowInput(false); }}>Изменить ответ</Button>
        </Stack>
      )}

      {showInput && state === "" && (
        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} alignItems="center">
          <Input size="sm" type="number" value={expected} onChange={(e) => setExpected(e.target.value)}
                 endDecorator="₽" placeholder="Сумма, которую ожидали" sx={{ maxWidth: 220 }} />
          <Button size="sm" variant="solid" color="danger" loading={busy}
                  onClick={() => {
                    const v = Number(expected);
                    if (!v || v <= 0) { notify?.("Укажите сумму больше нуля", "warning"); return; }
                    submit("disagree", v);
                  }}>Сохранить</Button>
        </Stack>
      )}
    </Box>
  );
}

/**
 * Только показ (для МЕНЕДЖЕРА и раздела «Платежи»).
 * props: agreement, expectedAmount, size
 */
export function AmountAgreementBadge({ agreement, expectedAmount, size = "sm" }) {
  // Разрешаем перенос текста и небольшой отступ сверху — чтобы длинные подписи
  // («Не согласен · ожидал 3 900,00 ₽») помещались в узкой колонке и не слипались.
  const chipSx = { mt: 0.5, height: 'auto', maxWidth: '100%', '--Chip-minHeight': '22px', py: 0.25 };
  const labelProps = { sx: { whiteSpace: 'normal', lineHeight: 1.25, display: 'block', textAlign: 'center' } };
  if (!agreement)
    return (
      <Chip size={size} variant="soft" color="neutral" sx={chipSx} slotProps={{ label: labelProps }}>
        Сумма не подтверждена
      </Chip>
    );
  if (agreement === "agree")
    return (
      <Chip size={size} variant="soft" color="success" sx={chipSx} slotProps={{ label: labelProps }}>
        Согласен с суммой
      </Chip>
    );
  return (
    <Chip size={size} variant="soft" color="danger" sx={chipSx} slotProps={{ label: labelProps }}>
      Не согласен · ожидал {fmt(expectedAmount)} ₽
    </Chip>
  );
}